import React, { useState, useEffect, useImperativeHandle, forwardRef, useRef, useMemo, useCallback } from 'react';
import axios from 'axios';
import {
  LiveKitRoom,
  useLocalParticipant,
  useRoomContext,
  useTracks,
  RoomAudioRenderer,
  useAudioPlayback,
  AudioTrack,
  useDisconnectButton,
  useRemoteParticipants
} from '@livekit/components-react';
import { Track, RoomEvent, LogLevel, setLogLevel } from 'livekit-client';
import './VoiceInput.css';
// import useVoiceSessionTracker from './VoiceSessionTracker'; // Removed - timer-based tracking commented out
import Branding from './Branding';
import VideoCollaborationPanel from './VideoCollaborationPanel';
import ToastNotification from './ToastNotification';
import MiraChatInterface from './MiraChatInterface';
import PricingTiers from './PricingTiers';

// FERPA Compliance: Suppress verbose SDK logs in production to avoid exposing room/session data
// In development, use 'warn' for debugging; in production, use 'error' or 'silent'
const isProduction = process.env.NODE_ENV === 'production';
setLogLevel(isProduction ? LogLevel.error : LogLevel.warn);

// Get LiveKit server URL from the environment or try to fetch it from backend when component mounts
const LIVEKIT_URL = process.env.REACT_APP_LIVEKIT_URL;
// Use dynamic room names instead of fixed test room to avoid multiple agents connecting to the same room

// Context for sharing state between LiveKitVoiceInput and RoomComponent
// CRITICAL: This prevents RoomComponent from being recreated on every parent render
// which was causing video flickering when API polls complete
const LiveKitStateContext = React.createContext(null);

// RoomComponent extracted to module scope to prevent recreation on parent re-renders
// This is the proper React pattern - defining components inside other components
// causes them to be treated as new component types on each render, triggering unmount/remount
const RoomComponentInner = React.memo(function RoomComponentInner() {
  // Get all shared state from context instead of closures
  const ctx = React.useContext(LiveKitStateContext);

  // LiveKit hooks - MUST be called unconditionally before any early returns
  const room = useRoomContext();
  const { localParticipant, isCameraEnabled } = useLocalParticipant();
  const { buttonProps } = useDisconnectButton({ stopTracks: true });
  const remoteParticipants = useRemoteParticipants();
  const speakingTimeoutRef = useRef(null);

  // Video tracks - using LiveKit's recommended pattern
  const rawVideoTracks = useTracks([
    { source: Track.Source.Camera, withPlaceholder: false }
  ], { onlySubscribed: false });

  const rawScreenShareTracks = useTracks([
    { source: Track.Source.ScreenShare, withPlaceholder: false }
  ], { onlySubscribed: false });

  // Remote audio tracks from AI agent
  const rawRemoteTracks = useTracks(
    [
      { source: Track.Source.Microphone },
      { source: Track.Source.Unknown }
    ],
    { onlySubscribed: true }
  );

  // Audio playback setup
  const { canPlayAudio, startAudio } = useAudioPlayback(room);

  // Track agent
  const isAgentParticipant = (p) => p.kind === 4 || p.kind === 'AGENT' || p.identity?.startsWith('agent-');
  const agentParticipant = remoteParticipants.find(isAgentParticipant);
  const agentIdentity = agentParticipant?.identity;

  // Extract context values with defaults for when context is not ready
  const {
    localParticipantRef = { current: null },
    roomRef = { current: null },
    videoTracksRef = { current: [] },
    screenShareTracksRef = { current: [] },
    callbackRefs = { current: {} },
    shownToastParticipants = { current: new Set() },
    friendEventCallbacks = { current: null },
    isAgentJoined = false,
    mode = 'voice',
    isSwitchingMode = false,
    userRequestedDisconnect = false,
    classroomSessionId = null,
    forceMinimizeVideoPanel = false,
    showVideoPanel = false,
    friendParticipants = [],
    isMuted = false,
    setIsAgentJoined = () => {},
    setAgentName = () => {},
    setAudioEnabled = () => {},
    setMode = () => {},
    setToken = () => {},
    setIsConnected = () => {},
    setUserInitiatedConnect = () => {},
    setError = () => {},
    setIsSwitchingMode = () => {},
    setShowVideoPanel = () => {},
    setFriendParticipantsRaw = () => {},
    setToastNotification = () => {},
    setUserRequestedDisconnect = () => {},
    componentRef = null
  } = ctx || {};

  // Detect agent join
  useEffect(() => {
    if (!ctx) return;
    if (agentIdentity && !isAgentJoined) {
      setIsAgentJoined(true);
      setAgentName('Mira');
      setAudioEnabled(true);
    }
  }, [ctx, agentIdentity, isAgentJoined, setIsAgentJoined, setAgentName, setAudioEnabled]);

  // Handle agent speaking state changes
  useEffect(() => {
    if (!ctx || !agentParticipant || !isAgentJoined) return;

    const handleSpeakingChanged = () => {
      if (agentParticipant.isSpeaking) {
        if (speakingTimeoutRef.current) {
          clearTimeout(speakingTimeoutRef.current);
          speakingTimeoutRef.current = null;
        }
        callbackRefs.current?.onAgentStateChange?.('speaking');
      } else {
        if (!speakingTimeoutRef.current) {
          speakingTimeoutRef.current = setTimeout(() => {
            callbackRefs.current?.onAgentStateChange?.('listening');
            speakingTimeoutRef.current = null;
          }, 800);
        }
      }
    };

    handleSpeakingChanged();
    room?.on(RoomEvent.ActiveSpeakersChanged, handleSpeakingChanged);

    return () => {
      room?.off(RoomEvent.ActiveSpeakersChanged, handleSpeakingChanged);
      if (speakingTimeoutRef.current) {
        clearTimeout(speakingTimeoutRef.current);
      }
    };
  }, [ctx, agentParticipant, isAgentJoined, room, callbackRefs]);

  // Update refs when localParticipant or room changes
  useEffect(() => {
    if (!ctx) return;
    localParticipantRef.current = localParticipant;
    roomRef.current = room;
  }, [ctx, localParticipant, room, localParticipantRef, roomRef]);

  // Mode switching function
  const switchMode = useCallback(async (newMode) => {
    if (!ctx || isSwitchingMode || newMode === mode) return;

    try {
      setIsSwitchingMode(true);

      await room.disconnect();
      setIsConnected(false);
      setMode(newMode);
      setToken('');
      await new Promise(resolve => setTimeout(resolve, 300));
      setUserInitiatedConnect(true);
    } catch (error) {
      console.error('❌ Error switching mode:', error);
      setError(`Failed to switch mode: ${error.message}`);
    } finally {
      setIsSwitchingMode(false);
    }
  }, [ctx, mode, room, isSwitchingMode, setIsSwitchingMode, setIsConnected, setMode, setToken, setUserInitiatedConnect, setError]);

  // Expose switchMode to parent via ref
  useEffect(() => {
    if (!ctx) return;
    if (componentRef && typeof componentRef === 'object') {
      componentRef.current = {
        ...componentRef.current,
        switchMode
      };
    }
  }, [ctx, switchMode, componentRef]);

  // Stabilize video tracks - only update ref when track SIDs actually change
  const videoTracks = useMemo(() => {
    if (!ctx) return rawVideoTracks;
    const currentSids = rawVideoTracks.map(t => t.publication?.trackSid).filter(Boolean).sort().join(',');
    const prevSids = videoTracksRef.current.map(t => t.publication?.trackSid).filter(Boolean).sort().join(',');

    if (currentSids.length > 0 && currentSids !== prevSids) {
      videoTracksRef.current = rawVideoTracks;
    }
    return videoTracksRef.current.length > 0 ? videoTracksRef.current : rawVideoTracks;
  }, [ctx, rawVideoTracks, videoTracksRef]);

  // Stabilize screen share tracks
  const screenShareTracks = useMemo(() => {
    if (!ctx) return rawScreenShareTracks;
    const currentSids = rawScreenShareTracks.map(t => t.publication?.trackSid).filter(Boolean).sort().join(',');
    const prevSids = screenShareTracksRef.current.map(t => t.publication?.trackSid).filter(Boolean).sort().join(',');

    if (currentSids.length > 0 && currentSids !== prevSids) {
      screenShareTracksRef.current = rawScreenShareTracks;
    }
    return screenShareTracksRef.current.length > 0 ? screenShareTracksRef.current : rawScreenShareTracks;
  }, [ctx, rawScreenShareTracks, screenShareTracksRef]);

  // Friend participants for video panel
  // Include ALL human participants (exclude only AI agents)
  // This ensures host can see friends AND friends can see host
  const videoPanelFriends = useMemo(() => {
    return remoteParticipants.filter(p => {
      // Exclude AI agents (they don't have video)
      const isAgent = p.kind === 4 || p.kind === 'AGENT' || p.identity?.startsWith('agent-');
      return !isAgent;
    });
  }, [remoteParticipants]);

  // Filter remote tracks for audio
  const remoteTracks = useMemo(() => {
    return rawRemoteTracks.filter(track => {
      const isRemote = track.participant.identity !== room?.localParticipant?.identity;
      const isAudio = track.publication.kind === Track.Kind.Audio;
      return isRemote && isAudio;
    });
  }, [rawRemoteTracks, room]);

  // Friend participant tracking
  useEffect(() => {
    if (!ctx) return;
    if (friendParticipants.length === 1) {
      setShowVideoPanel(true);
    }
  }, [ctx, friendParticipants.length, setShowVideoPanel]);

  // Handle user-requested disconnect
  useEffect(() => {
    if (!ctx) return;
    if (userRequestedDisconnect && buttonProps?.onClick) {
      buttonProps.onClick();
      setUserRequestedDisconnect(false);
    }
  }, [ctx, userRequestedDisconnect, buttonProps, setUserRequestedDisconnect]);

  // Handle microphone mute/unmute based on isMuted prop from parent
  useEffect(() => {
    if (!ctx || !localParticipant) return;

    const audioTrack = localParticipant.getTrack(Track.Source.Microphone);
    if (audioTrack) {
      if (isMuted) {
        audioTrack.mute();
      } else {
        audioTrack.unmute();
      }
    }
  }, [ctx, localParticipant, isMuted]);

  // Room event handlers
  useEffect(() => {
    if (!ctx || !room) return;

    const handleParticipantConnected = (participant) => {
      if (participant.identity.startsWith('friend_')) {
        // FERPA: Use participant.name from LiveKit token only (no PII in identity)
        const friendName = (participant.name && participant.name !== 'undefined')
          ? participant.name
          : 'Study Buddy';

        setFriendParticipantsRaw(prev => [...prev, participant]);

        if (friendEventCallbacks.current?.onFriendJoined) {
          friendEventCallbacks.current.onFriendJoined(participant);
        }

        if (!shownToastParticipants.current.has(participant.identity)) {
          setToastNotification({
            message: `${friendName} joined! Video collaboration available 📹`,
            type: 'friend-joined',
            duration: 4000
          });
          shownToastParticipants.current.add(participant.identity);
        }

        setTimeout(() => setShowVideoPanel(true), 1500);
      }

      const isAgent = participant.kind === 4 || participant.kind === 'AGENT' || participant.identity?.startsWith('agent-');
      if (isAgent) {
        setIsAgentJoined(true);
        const displayName = participant.name || participant.identity.replace('agent-', '').split('_')[0] || 'Mira';
        setAgentName(displayName.charAt(0).toUpperCase() + displayName.slice(1));
        setAudioEnabled(true);
      }
    };

    const handleParticipantDisconnected = (participant) => {
      if (participant.identity.startsWith('friend_')) {
        setFriendParticipantsRaw(prev => prev.filter(p => p.identity !== participant.identity));
        if (friendEventCallbacks.current?.onFriendLeft) {
          friendEventCallbacks.current.onFriendLeft(participant);
        }
      }

      const isAgent = participant.kind === 4 || participant.kind === 'AGENT' || participant.identity?.startsWith('agent-');
      if (isAgent) {
        setIsAgentJoined(false);
        callbackRefs.current?.onAgentStateChange?.('disconnected');
      }
    };

    // Handle data channel messages (visualizations, web search results, etc.)
    const handleDataMessageReceived = (message, topic) => {
      try {
        let data;
        // Convert message to string if it's Uint8Array
        if (message instanceof Uint8Array) {
          const decoder = new TextDecoder();
          data = JSON.parse(decoder.decode(message));
        } else {
          data = JSON.parse(message);
        }

        // Handle panel close commands
        if (data.type === 'close_panel' && callbackRefs.current?.onVisualizationRequest) {
          callbackRefs.current.onVisualizationRequest(data);
          return;
        }

        // Handle sound effects messages
        if (data.type === 'reading_sound_effects' && callbackRefs.current?.onSoundEffects) {
          callbackRefs.current.onSoundEffects(data);
          return;
        }

        // Handle sound control messages
        if (data.type === 'sound_control' && callbackRefs.current?.onSoundEffects) {
          callbackRefs.current.onSoundEffects(data);
          return;
        }

        // Handle token reward messages
        if ((topic === 'token_rewards' || data.type === 'token_reward') && callbackRefs.current?.onTokenReward) {
          callbackRefs.current.onTokenReward(data);
          return;
        }

        // Handle visualization messages (from topic or from message type)
        if ((topic === 'morpheus-visualization' || data.type === 'visualization') && callbackRefs.current?.onVisualizationRequest) {
          // Extract visualization data from the message
          const visualizationData = data.visualization || data;
          callbackRefs.current.onVisualizationRequest(visualizationData);
          return;
        }

      } catch (e) {
        console.error('Error parsing data channel message:', e);
      }
    };

    // Wrapper for data received events
    const handleDataReceivedEvent = (payload, participant, topic) => {
      try {
        handleDataMessageReceived(payload, topic || '');
      } catch (e) {
        console.error('Error handling data received:', e);
      }
    };

    room.on(RoomEvent.ParticipantConnected, handleParticipantConnected);
    room.on(RoomEvent.ParticipantDisconnected, handleParticipantDisconnected);
    room.on('dataReceived', handleDataReceivedEvent);

    return () => {
      room.off(RoomEvent.ParticipantConnected, handleParticipantConnected);
      room.off(RoomEvent.ParticipantDisconnected, handleParticipantDisconnected);
      room.off('dataReceived', handleDataReceivedEvent);
    };
  }, [ctx, room, setFriendParticipantsRaw, setToastNotification, setShowVideoPanel, setIsAgentJoined, setAgentName, setAudioEnabled, shownToastParticipants, callbackRefs]);

  // If context not ready, render nothing (after all hooks are called)
  if (!ctx) return null;

  // Render based on mode
  if (mode === 'classroom') {
    return (
      <div className="livekit-room-container">
        <MiraChatInterface
          classroomSessionId={classroomSessionId}
          onActivityUpdate={() => {}}
        />
      </div>
    );
  }

  // Voice mode render
  return (
    <div className="livekit-room-container">
      <RoomAudioRenderer volume={0.6} />

      <VideoCollaborationPanel
        show={showVideoPanel}
        onClose={() => {
          const currentRoom = roomRef.current;
          if (currentRoom && friendParticipants.length > 0) {
            try {
              const encoder = new TextEncoder();
              const message = encoder.encode(JSON.stringify({
                type: 'collaboration_ended',
                ended_by: 'host',
                timestamp: Date.now()
              }));
              // Use updated LiveKit API - reliable is default
              currentRoom.localParticipant.publishData(message);
            } catch (err) {
              console.error('Failed to send end session message:', err);
            }
          }
          setShowVideoPanel(false);
          setFriendParticipantsRaw([]);
          shownToastParticipants.current.clear();
        }}
        forceMinimize={forceMinimizeVideoPanel}
        localParticipant={localParticipant}
        isCameraEnabled={isCameraEnabled}
        videoTracks={videoTracks}
        screenShareTracks={screenShareTracks}
        friendParticipants={videoPanelFriends}
      />

      {/* Audio tracks */}
      {remoteTracks.map((track) => {
        if (track.publication.kind !== Track.Kind.Audio) return null;
        return (
          <AudioTrack
            key={`audio-${track.publication.trackSid}`}
            participant={track.participant}
            source={track.source}
            publication={track.publication}
            volume={0.6}
          />
        );
      })}

      {/* Audio playback button for autoplay restrictions */}
      {!canPlayAudio && (
        <button
          onClick={startAudio}
          style={{
            position: 'fixed',
            bottom: '20px',
            left: '50%',
            transform: 'translateX(-50%)',
            padding: '12px 24px',
            background: 'linear-gradient(135deg, #297A92 0%, #42909E 100%)',
            color: 'white',
            border: 'none',
            borderRadius: '12px',
            cursor: 'pointer',
            zIndex: 1000,
            fontWeight: 600
          }}
        >
          🔊 Enable Audio
        </button>
      )}
    </div>
  );
});

const LiveKitVoiceInput = forwardRef(({ onTranscript, onVisualizationRequest, onSoundEffects, onTokenReward, isDisabled = false, onVoiceLimitReached = () => {}, userClicked = false, isMuted = false, userTier = 'guest', user = null, showVideoCollaboration = false, forceMinimizeVideoPanel = false, classroomMode = false, classroomSessionId = null, silentMode = false, onSilentModeChange = () => {}, teachItBackMode = false, teachTopic = null, clutchTimeMode = false, clutchTimeSubject = null, clutchTimeTopics = [], clutchTimeTestDate = null, sessionLanguage = 'en', onConnectionChange, onAgentJoinedChange, onAgentStateChange, onMuteChange, onFriendsChange }, ref) => {
  // LiveKit state
  const [token, setToken] = useState('');
  const [roomName, setRoomName] = useState('');
  const [isConnecting, setIsConnecting] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState(null);
  const [showPricingModal, setShowPricingModal] = useState(false);
  const [serverUrl, setServerUrl] = useState(LIVEKIT_URL || null);
  // Use isDisabled prop directly instead of internal state
  
  // Ref to store localParticipant from RoomComponent
  const localParticipantRef = useRef(null);

  // Ref to store room for sending data messages
  const roomRef = useRef(null);

  // Ref to prevent double token fetch (React StrictMode/race condition guard)
  const tokenFetchInProgress = useRef(false);

  // Ref to store friend event callbacks (prevents render loops)
  const friendEventCallbacks = useRef(null);

  // IMPORTANT: Video track refs at parent level to persist across RoomComponent remounts
  // RoomComponent is recreated on every parent render, so refs inside it get reset
  const videoTracksRef = useRef([]);
  const screenShareTracksRef = useRef([]);

  // UI state
  const [isAgentJoined, setIsAgentJoined] = useState(false);
  const [agentName, setAgentName] = useState('Mira');
  const [audioEnabled, setAudioEnabled] = useState(false); // Track if audio playback is enabled

  /**
   * User Identity for LiveKit Sessions
   *
   * FERPA COMPLIANCE:
   * - Authenticated users: Use their database ID (no PII in the ID itself)
   * - Guest users: Use persistent anonymous ID from localStorage
   *
   * WHY PERSISTENT IDs ARE FERPA-COMPLIANT:
   * 1. IDs are randomly generated (contain no PII)
   * 2. Used only for rate limiting/paywall enforcement (legitimate business purpose)
   * 3. Data stays on our servers only (not shared with third parties)
   * 4. No educational records are associated with these IDs
   *
   * WHY PERSISTENT IDs ARE REQUIRED:
   * - Backend enforces 3-session limit for guests via Redis
   * - Without persistent IDs, users could bypass paywall by refreshing
   * - Key format: guest_session_count:mira_guest:{userId}
   */
  const [userId] = useState(() => {
    // For authenticated users, use their actual ID
    if (user?.id) {
      return `user-${user.id}`;
    }

    // For guests, use persistent localStorage ID (matches TutorInterface pattern)
    // This ensures paywall tracking works across page refreshes
    try {
      const ANON_ID_KEY = 'morpheus_anon_id';
      let anonId = localStorage.getItem(ANON_ID_KEY);

      if (!anonId) {
        // Generate new ID matching the pattern used throughout the app
        anonId = `anon-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
        localStorage.setItem(ANON_ID_KEY, anonId);
      }

      return anonId;
    } catch (e) {
      // localStorage unavailable (private browsing, storage quota, etc.)
      // Fall back to session-only ID - paywall won't persist but service still works
      // This is acceptable graceful degradation
      console.warn('[LiveKit] localStorage unavailable, using session-only ID:', e.message);
      return `guest-${Date.now()}-${Math.random().toString(36).substring(2, 8)}`;
    }
  });
  
  const [sessionId, setSessionId] = useState(() => `session-${Math.random().toString(36).substring(2, 15)}`);
  const [userInitiatedConnect, setUserInitiatedConnect] = useState(false); // Flag for user initiated connection

  // Mode state - voice, classroom, teach_it_back, or clutch_time
  const [mode, setMode] = useState(
    classroomMode ? 'classroom' : (teachItBackMode ? 'teach_it_back' : (clutchTimeMode ? 'clutch_time' : 'voice'))
  );
  const [isSwitchingMode, setIsSwitchingMode] = useState(false);

  // Sync internal mode state with mode props
  useEffect(() => {
    let newMode = 'voice';
    if (classroomMode) {
      newMode = 'classroom';
    } else if (teachItBackMode) {
      newMode = 'teach_it_back';
    } else if (clutchTimeMode) {
      newMode = 'clutch_time';
    }
    if (newMode !== mode) {
      setMode(newMode);
    }
  }, [classroomMode, teachItBackMode, clutchTimeMode, mode]);

  // Silent Mode: Send data message to agent when silent mode changes
  // This toggles audio input/output on the agent side without reconnecting
  useEffect(() => {
    const room = roomRef.current;
    if (!room || !room.localParticipant) return;

    try {
      const encoder = new TextEncoder();
      const message = encoder.encode(JSON.stringify({
        type: 'set_silent_mode',
        silent: silentMode
      }));
      room.localParticipant.publishData(message);
      console.log(`🔇 Silent Mode ${silentMode ? 'enabled' : 'disabled'} - sent to agent`);

      // Also mute/unmute local audio tracks
      room.localParticipant.audioTrackPublications.forEach(pub => {
        if (pub.track) {
          if (silentMode) {
            pub.track.mute();
          } else {
            pub.track.unmute();
          }
        }
      });
    } catch (err) {
      console.error('Failed to send silent mode message:', err);
    }
  }, [silentMode]);

  // State to track user-requested disconnect
  const [userRequestedDisconnect, setUserRequestedDisconnect] = useState(false);
  
  // Video collaboration panel state - controlled by parent (TutorInterface)
  const [showVideoPanel, setShowVideoPanel] = useState(false);

  // Toast notification state
  const [toastNotification, setToastNotification] = useState(null);
  const shownToastParticipants = useRef(new Set());
  
  // Memoized toast close handler to prevent re-renders
  const handleToastClose = useCallback(() => {
    setToastNotification(null);
  }, []);
  
  // Friend upgrade modal state
  const [showFriendUpgrade, setShowFriendUpgrade] = useState(false);
  const [friendParticipantsRaw, setFriendParticipantsRaw] = useState([]);
  
  // Memoize friendParticipants to prevent re-renders when array contents haven't changed
  const friendParticipants = useMemo(() => friendParticipantsRaw, [
    friendParticipantsRaw.length,
    JSON.stringify(friendParticipantsRaw.map(p => p.identity).sort())
  ]);

  // Show video panel when collaboration is enabled AND friends are present
  useEffect(() => {
    if (showVideoCollaboration && friendParticipants.length > 0) {
      setShowVideoPanel(true);
    }
  }, [showVideoCollaboration, friendParticipants.length]);

  // Memoize LiveKit options to prevent re-renders
  const liveKitOptions = useMemo(() => ({
    adaptiveStream: true,
    dynacast: true,
    publishDefaults: {
      simulcast: true,
      audioEnabled: true,
      videoEnabled: false,
      dtx: true
    },
    audioCaptureDefaults: {
      echoCancellation: true,
      noiseSuppression: true,
      autoGainControl: true
    },
    autoSubscribe: true,
    connectionDetails: {
      maxRetries: 3
    },
    trackSubscriptionDefaults: {
      subscribedCodecs: [],
      audio: true
    },
    enableTranscriptions: true,
    enableChat: true
  }), []);
  
  // Helper function to get friend display name
  // FERPA: Only use participant.name from LiveKit token (no PII parsing from identity)
  const getFriendDisplayName = (participant) => {
    if (!participant) return 'Study Buddy';
    // Use participant.name from LiveKit token (set via .with_name())
    if (participant.name && participant.name !== 'undefined') {
      return participant.name;
    }
    // FERPA: Don't parse names from identity - use generic fallback
    return 'Study Buddy';
  };

  // ============================================================
  // Phase 2: FaceTime with Mira - Callback hooks for parent state sync
  // These notify TutorInterface of connection/agent state changes
  //
  // IMPORTANT: Use refs to store callbacks and previous values to prevent
  // render loops. Inline callbacks get recreated every render, which would
  // trigger useEffect continuously if they were in the dependency array.
  // ============================================================

  // Store ALL callback props in refs to avoid dependency array issues
  // This prevents render loops when parent re-renders with new inline function identities
  const callbackRefs = useRef({
    onConnectionChange,
    onAgentJoinedChange,
    onFriendsChange,
    onVisualizationRequest,
    onTranscript,
    onSoundEffects,
    onTokenReward,
    onAgentStateChange
  });

  // Update refs when callbacks change (but don't trigger effects)
  useEffect(() => {
    callbackRefs.current = {
      onConnectionChange,
      onAgentJoinedChange,
      onFriendsChange,
      onVisualizationRequest,
      onTranscript,
      onSoundEffects,
      onTokenReward,
      onAgentStateChange
    };
  });

  // Track previous values to only call callbacks on actual changes
  const prevIsConnected = useRef(isConnected);
  const prevIsAgentJoined = useRef(isAgentJoined);
  const prevFriendCount = useRef(0);

  // Notify parent when connection state changes
  useEffect(() => {
    if (prevIsConnected.current !== isConnected) {
      prevIsConnected.current = isConnected;
      // Pass roomName along with connection status so parent can update presence
      callbackRefs.current.onConnectionChange?.(isConnected, roomName);
    }
  }, [isConnected, roomName]);

  // Notify parent when agent joins/leaves (critical: Mira must be ready before user talks)
  useEffect(() => {
    if (prevIsAgentJoined.current !== isAgentJoined) {
      prevIsAgentJoined.current = isAgentJoined;
      callbackRefs.current.onAgentJoinedChange?.(isAgentJoined);
    }
  }, [isAgentJoined]);

  // Notify parent when friend participants change
  useEffect(() => {
    const currentCount = friendParticipants?.length || 0;
    if (prevFriendCount.current !== currentCount && friendParticipants) {
      prevFriendCount.current = currentCount;
      const friendsData = friendParticipants.map(p => ({
        id: p.identity,
        name: getFriendDisplayName(p),
        isSpeaking: p.isSpeaking || false,
        avatar: null
      }));
      callbackRefs.current.onFriendsChange?.(friendsData);
    }
  }, [friendParticipants]);

  // Fetch the LiveKit URL from the server if not in environment
  useEffect(() => {
    // Hard-code a default server URL if there's no environment variable
    // This is a fallback to ensure the UI shows even if API is not available
    if (!serverUrl) {
      // Default to wss://localhost:7880 as a fallback for development
      setServerUrl(LIVEKIT_URL || 'wss://localhost:7880');
    }
    
    // Try to fetch the proper URL, but don't block UI on it
    const fetchLiveKitConfig = async () => {
      try {
        const response = await axios.get('/api/livekit/test');
        if (response.data && response.data.success && 
            response.data.server_config && response.data.server_config.url_configured) {
          // Extract the URL from the response (this depends on your API structure)
          const fetchedUrl = response.data.websocket_url || LIVEKIT_URL;
          setServerUrl(fetchedUrl);
        } else {
          // We already set a default above, so UI will still show
        }
      } catch (err) {
        console.warn('Failed to retrieve LiveKit configuration, using default URL');
        // We already set a default URL, so UI will still show
      }
    };
    
    fetchLiveKitConfig();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  
  // Add iOS/Safari specific audio unlocking on component mount
  // This is critical since iOS has the strictest autoplay policies
  useEffect(() => {
    // Check if we're on iOS
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) || 
                 (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    
    // For iOS, we need special handling for audio
    if (isIOS) {
      
      // Function to unlock audio on iOS
      const unlockAudioOnIOS = () => {
        try {
          
          // Create a silent audio context
          const audioContext = new (window.AudioContext || window.webkitAudioContext)();
          const oscillator = audioContext.createOscillator();
          const gainNode = audioContext.createGain();
          gainNode.gain.value = 0.01; // Very low volume
          oscillator.frequency.value = 1; // Very low frequency
          oscillator.connect(gainNode);
          gainNode.connect(audioContext.destination);
          oscillator.start(0);
          oscillator.stop(0.1); // Very brief tone
          
        } catch (e) {
          console.warn('iOS audio unlock attempt failed:', e);
        }
      };
      
      // Add listeners for user interaction to unlock audio
      document.addEventListener('touchstart', unlockAudioOnIOS, { once: true });
      document.addEventListener('touchend', unlockAudioOnIOS, { once: true });
      document.addEventListener('click', unlockAudioOnIOS, { once: true });
      
      // Clean up event listeners when component unmounts
      return () => {
        document.removeEventListener('touchstart', unlockAudioOnIOS);
        document.removeEventListener('touchend', unlockAudioOnIOS);
        document.removeEventListener('click', unlockAudioOnIOS);
      };
    }
  }, []);
  
  // Create a unique session ID if one doesn't exist
  useEffect(() => {
    if (!sessionId) {
      const newSessionId = `session-${Math.random().toString(36).substring(2, 15)}`;
      setSessionId(newSessionId);
    }
  }, [sessionId]);
  
  // Only initiate the connection when the user has clicked the Mira button
  useEffect(() => {
    if (userClicked) {
      setUserInitiatedConnect(true);
    }
  }, [userClicked]);

  // State to track microphone permission status
  const [micPermissionStatus, setMicPermissionStatus] = useState('pending'); // 'pending', 'granted', 'denied'
  
  // Request microphone permissions - returns boolean
  const requestMicrophonePermissions = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        }
      });

      setMicPermissionStatus('granted');
      const hasAudio = stream.getAudioTracks().length > 0;
      // Stop the stream since LiveKit will request it again
      stream.getTracks().forEach(track => track.stop());
      return hasAudio;
    } catch (err) {
      console.error('❌ Microphone permission denied:', err);
      setMicPermissionStatus('denied');
      setError('Microphone access denied. Please enable microphone permissions to use voice features.');
      return false;
    }
  };
  
  // Check microphone permissions at component mount, but don't request them
  useEffect(() => {
    // Only check for existing permissions without requesting new ones
    if (navigator.permissions && navigator.permissions.query) {
      navigator.permissions.query({ name: 'microphone' })
        .then(permissionStatus => {
          // Only update if permission is already granted
          if (permissionStatus.state === 'granted') {
            setMicPermissionStatus('granted');
          } else {
            // Keep as pending to ensure user interaction is required
            setMicPermissionStatus('pending');
          }

          // Listen for permission changes
          permissionStatus.onchange = () => {
            setMicPermissionStatus(permissionStatus.state);
          };
        })
        .catch(err => {
          console.warn('Permission query not supported:', err);
          // Fall back to request on click
        });
    }
  }, []);

  // Initialize LiveKit room - but only after explicit user action
  useEffect(() => {
    
    const initializeLiveKit = async () => {
      // Don't connect unless user has explicitly requested it by clicking the mic button
      // This requires the user to click the Start Voice Session button first
      if (isDisabled || isConnecting || isConnected || token || (!userInitiatedConnect && !userClicked)) {
        return;
      }

      // Prevent double token fetch (React StrictMode/race condition guard)
      if (tokenFetchInProgress.current) {
        return;
      }
      tokenFetchInProgress.current = true;

      try {
        setIsConnecting(true);
        setError(null);

        // Calculate effective mode directly from props to avoid race condition
        // (mode state may not be synced yet when this runs)
        const effectiveMode = classroomMode ? 'classroom'
          : (teachItBackMode ? 'teach_it_back'
          : (clutchTimeMode ? 'clutch_time' : 'voice'));

        // Request microphone permissions first before attempting to connect
        // SKIP microphone permissions in classroom mode or silent mode (text-only, no audio)
        if (effectiveMode !== 'classroom' && !silentMode && micPermissionStatus !== 'granted') {
          const hasPermission = await requestMicrophonePermissions();
          if (!hasPermission) {
            setIsConnecting(false);
            return;
          }
        }

        // Request a token from our server with provider preference
        // Using the direct route as in the working project
        // IMPORTANT: Include withCredentials to send auth cookies
        // NOTE: Don't pre-generate room name - let the server determine it
        // Server returns room-user-{id} for authenticated users, ensuring consistency

        // Build request payload - include teach_topic for teach_it_back mode
        const tokenPayload = {
          identity: userId,  // Use persistent user ID instead of random session ID
          session_id: sessionId,  // Send session ID to allow room reuse
          mode: effectiveMode,  // Send mode (voice, classroom, teach_it_back, or clutch_time) for agent dispatch
          silent_mode: silentMode,  // Silent mode: start with audio disabled (for classroom use)
          language_preference: sessionLanguage,  // School-enabled bilingual support
          learner_type: user?.learner_type || 'student'  // School-enabled adult learner support
        };

        // Add teach_topic for teach_it_back mode (Feynman technique)
        if (effectiveMode === 'teach_it_back' && teachTopic) {
          tokenPayload.teach_topic = teachTopic;
        }

        // Notebook assignment session: pass the opaque share token from the
        // URL — the server resolves it to notebook ids (never trusted from
        // the client). Set when the student arrived via /assignment/<token>.
        try {
          const nbToken = new URLSearchParams(window.location.search).get('notebook_assignment');
          if (nbToken) {
            tokenPayload.notebook_assignment_token = nbToken;
          }
        } catch (e) { /* non-blocking */ }

        // Add clutch_time data for exam prep mode
        if (effectiveMode === 'clutch_time') {
          tokenPayload.clutch_time_subject = clutchTimeSubject;
          tokenPayload.clutch_time_topics = clutchTimeTopics;
          tokenPayload.clutch_time_test_date = clutchTimeTestDate;
        }

        const response = await axios.post('/get_token', tokenPayload, {
          withCredentials: true  // This ensures auth cookies are sent
        });

        if (response.data && response.data.token) {
          // Always use the room name from server response
          // Server generates consistent room names (room-user-{id} for authenticated users)
          const serverRoomName = response.data.roomName;
          if (serverRoomName) {
            setRoomName(serverRoomName);
          } else {
            // Fallback for legacy responses without roomName
            const fallbackRoomName = `morpheus-voice-${Math.random().toString(36).substring(2, 9)}`;
            setRoomName(fallbackRoomName);
          }

          setToken(response.data.token);
          setServerUrl(response.data.wsUrl || serverUrl);
        } else {
          console.error('Failed to get LiveKit token:', response.data?.error || 'Unknown error');
          setError(`Failed to connect: ${response.data?.error || 'Connection error'}`);
          setIsConnecting(false);
        }
      } catch (err) {
        console.error('Error initializing LiveKit:', err);

        // Check for session limit paywall (403 error)
        if (err.response && err.response.status === 403 && err.response.data?.error === 'session_limit_reached') {
          const message = err.response.data.message || "You've used your free sessions. Sign up to keep learning with Mira.";
          const gateType = err.response.data.gate_type || 'subscribe'; // 'create_account' or 'subscribe'
          const isAnonymous = err.response.data.is_anonymous || false;
          const sessionCount = err.response.data.session_count || 0;
          setError(message);
          setUserInitiatedConnect(false);  // Prevent retry loop
          onVoiceLimitReached({ gateType, isAnonymous, sessionCount, message });  // Notify parent to show appropriate gate
          return;  // Exit early, don't set other errors
        }
        // Check for specific error types to provide better error messages
        else if (err.message && err.message.includes('ECONNREFUSED')) {
          setError('Cannot connect to server. Is the Flask backend running?');
        } else if (err.message && err.message.includes('Network Error')) {
          setError('Network error: Check if the server is running and accessible');
        } else if ((err.message && err.message.includes('Permission denied')) ||
                  (err.message && err.message.includes('NotAllowedError'))) {
          setError('Microphone permission denied. Please enable your microphone to use voice features.');
        } else {
          setError(`Connection error: ${err.message}`);
        }

        // For proxy errors, show a special message
        if (err.response && err.response.status === 502) {
          setError('Proxy error: The server might not be running');
        }
      } finally {
        setIsConnecting(false);
        tokenFetchInProgress.current = false;
      }
    };

    initializeLiveKit();
  }, [isDisabled, isConnecting, isConnected, token, sessionId, micPermissionStatus, userInitiatedConnect, serverUrl]);


  // Handle LiveKit room connection events
  const handleRoomConnected = useCallback(() => {
    setIsConnected(true);
    setIsConnecting(false);
    
    // Enable audio immediately upon connection
    // This helps with browser autoplay policies
    try {
      // Create and play a silent audio context as a backup method
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      audioContext.resume().catch(e => {
        console.error('❌ Audio context resume failed:', e);
      });
      
      // Play base64 encoded silent sound to unlock audio on browsers with strict autoplay policies
      const silentSound = document.createElement('audio');
      silentSound.setAttribute('src', 'data:audio/wav;base64,UklGRigAAABXQVZFZm10IBIAAAABAAEARKwAAIhYAQACABAAAABkYXRhAgAAAAEA');
      silentSound.volume = 0.1;

      // Try to unlock audio
      const unlockAudio = async () => {
        try {
          await silentSound.play();
        } catch (e) {
          // Silent audio unlock failed - this is expected on some browsers until user interaction
        }

        // Set audio as enabled regardless of success/failure
        // This forces LiveKit to attempt audio playback
        setAudioEnabled(true);
      };
      
      // Run the audio unlock function
      unlockAudio();
      
      // Add a click listener to the document to unlock audio on first user interaction
      const unlockOnClick = async () => {
        await unlockAudio();
        document.removeEventListener('click', unlockOnClick);
      };
      document.addEventListener('click', unlockOnClick);
      
    } catch (e) {
      console.error('❌ Audio context initialization error:', e);
    }
    
    // Agent automatically dispatches via token-based dispatch (RoomAgentDispatch)
    if (!roomName) {
      console.warn('⚠️ No room name available');
    }
    
    // Investment-based approach: No time limits, focus on value delivery
    // Session is initialized during token generation - no additional tracking call needed
  }, [roomName, sessionId]);
  
  const handleRoomDisconnected = useCallback(() => {
    setIsConnected(false);

    // Reset agent joined state - critical for callback to fire on reconnect
    setIsAgentJoined(false);

    // End voice session tracking
    // Session ended - investment psychology approach

    // End session tracking on the server
    if (sessionId) {
      try {
        axios.post('/api/voice/end_session', {
          session_id: sessionId,
          room_name: roomName
        }).then(response => {
          if (response.data && !response.data.success) {
            console.warn('Failed to end session on server:', response.data);
          }
        }).catch(error => {
          console.error('Error ending session on server:', error);
        });
      } catch (e) {
        console.error('Failed to end session on server:', e);
      }
    }

    // Clear session state for clean disconnect
    // Clear token but don't trigger auto-reconnect
    setToken('');

    // Clear any error messages when disconnecting normally
    setError(null);
  }, [sessionId, roomName]);
  
  // NOTE: useImperativeHandle is defined once below (around line 769) with ALL methods
  // including disconnect. Do NOT add another useImperativeHandle here as it will overwrite.
  
  const handleRoomError = useCallback((error) => {
    console.error('LiveKit room error:', error);
    // Only set error if it's not during normal disconnection
    if (!isDisabled) {
      setError(`Room error: ${error.message}`);
    }
    setIsConnected(false);
    setIsConnecting(false);
    
    // End voice session tracking on error
    // Session ended - investment psychology approach

    // End session tracking on the server due to error
    if (sessionId) {
      try {
        axios.post('/api/voice/end_session', {
          session_id: sessionId,
          room_name: roomName
        }).then(response => {
          if (response.data && !response.data.success) {
            console.warn('Failed to end session on server:', response.data);
          }
        }).catch(error => {
          console.error('Error ending session on server:', error);
        });
      } catch (e) {
        console.error('Failed to end session on server:', e);
      }
    }
    
    // Don't automatically reconnect on error either
    // We'll let the user decide when to reconnect
    setToken('');
  }, [isDisabled, sessionId, roomName]);
  
  // Expose methods to the parent component via the ref
  // IMPORTANT: Only ONE useImperativeHandle call - multiple calls overwrite each other!
  useImperativeHandle(ref, () => ({
    // Disconnect method - triggers LiveKit room disconnect
    disconnect: () => {
      // Set a flag that the RoomComponent will use to trigger disconnect
      setUserRequestedDisconnect(true);
    },
    isConnectedToRoom: () => isConnected,
    // Get current room information for sharing
    getRoomInfo: () => ({
      roomName: roomName,
      isConnected: isConnected,
      serverUrl: serverUrl,
      localParticipant: localParticipantRef.current
    }),
    // Event-driven friend tracking (replaces polling getFriendParticipants)
    registerFriendEvents: (callbacks) => {
      friendEventCallbacks.current = callbacks;
    },
    // Expose the visualization request handler to allow direct testing
    onVisualizationRequest: (data) => {
      if (callbackRefs.current.onVisualizationRequest) {
        callbackRefs.current.onVisualizationRequest(data);
        return true;
      }
      return false;
    },
    // Send text message to agent (for Silent Mode)
    sendTextMessage: async (text) => {
      const room = roomRef.current;
      if (!room || !room.localParticipant || !text.trim()) {
        console.warn('Cannot send text message: room not connected or empty message');
        return false;
      }
      try {
        const encoder = new TextEncoder();
        const message = encoder.encode(text.trim());
        // publishData(data, kind, options) - kind: 0=RELIABLE, 1=LOSSY
        await room.localParticipant.publishData(message, 0, { topic: 'lk.chat' });
        console.log('📝 Sent text message to agent:', text.substring(0, 50));
        return true;
      } catch (err) {
        console.error('Failed to send text message:', err);
        return false;
      }
    },
  }));

  // Memoize context value to prevent unnecessary re-renders of RoomComponentInner
  // State setters are stable and don't need to be in deps array
  const contextValue = useMemo(() => ({
    // Refs (stable)
    localParticipantRef,
    roomRef,
    videoTracksRef,
    screenShareTracksRef,
    callbackRefs,
    shownToastParticipants,
    friendEventCallbacks,
    // State values (change triggers re-render)
    isAgentJoined,
    mode,
    isSwitchingMode,
    userRequestedDisconnect,
    classroomSessionId,
    forceMinimizeVideoPanel,
    showVideoPanel,
    userTier,
    user,
    showFriendUpgrade,
    friendParticipants,
    isMuted,
    // State setters (stable, from useState)
    setIsAgentJoined,
    setAgentName,
    setAudioEnabled,
    setMode,
    setToken,
    setIsConnected,
    setUserInitiatedConnect,
    setError,
    setIsSwitchingMode,
    setShowVideoPanel,
    setFriendParticipantsRaw,
    setToastNotification,
    setUserRequestedDisconnect,
    setShowFriendUpgrade,
    // Callbacks
    onVisualizationRequest,
    // Component ref
    componentRef: ref
  }), [
    isAgentJoined, mode, isSwitchingMode, userRequestedDisconnect,
    classroomSessionId, forceMinimizeVideoPanel, showVideoPanel,
    userTier, user, showFriendUpgrade, friendParticipants, isMuted,
    onVisualizationRequest, ref
  ]);

  return (
    <div className="voice-input livekit-voice-input" style={{
      display: mode === 'classroom' ? 'block' : 'none', /* Show in classroom mode, hide in voice mode */
      position: mode === 'classroom' ? 'relative' : 'absolute',
      opacity: mode === 'classroom' ? 1 : 0,
      width: mode === 'classroom' ? '100%' : '1px',
      height: mode === 'classroom' ? 'auto' : '1px',
      overflow: mode === 'classroom' ? 'visible' : 'hidden',
      pointerEvents: mode === 'classroom' ? 'auto' : 'none',
      /* Keep these settings for backward compatibility but they won't be visible */
      border: isConnected 
        ? `${Branding.borders.width.medium} solid ${Branding.colors.tealBlue}` 
        : `${Branding.borders.width.thin} solid ${Branding.colors.veryLightGray}`,
      borderRadius: Branding.borders.radius.lg,
      padding: Branding.spacing.md,
      marginBottom: Branding.spacing.md,
      backgroundColor: 'transparent', /* Make background transparent */
      boxShadow: 'none', /* Remove shadow */
      transition: Branding.transitions.default
    }}>
      {/* Only show critical error messages when not in disabled state */}
      {error && !isDisabled && (
        <div className="voice-error visible" style={{
          // Show session limit paywall prominently, hide other errors
          display: error.includes("You've used 3 free sessions") ? 'block' : 'none',
          backgroundColor: '#fff3cd',
          border: '2px solid #ffc107',
          borderRadius: '8px',
          padding: '20px',
          margin: '20px 0',
          textAlign: 'center'
        }}>
          {error.includes("You've used 3 free sessions") ? (
            <div>
              <div style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '10px', color: '#856404' }}>
                🎓 Ready to unlock unlimited learning?
              </div>
              <div style={{ fontSize: '14px', marginBottom: '15px', color: '#856404' }}>
                {error}
              </div>
              <button
                onClick={() => setShowPricingModal(true)}
                style={{
                  display: 'inline-block',
                  padding: '12px 24px',
                  backgroundColor: '#4158D0',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  fontWeight: 'bold',
                  fontSize: '16px',
                  cursor: 'pointer',
                  transition: 'background-color 0.2s'
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#3646b8'}
                onMouseLeave={(e) => e.target.style.backgroundColor = '#4158D0'}
              >
                View Pricing Options
              </button>
            </div>
          ) : (
            error
          )}
        </div>
      )}
      
      {/* Show connecting message while establishing connection */}
      {isConnecting && (
        <div className="connecting-message">
          Connecting to voice service...
        </div>
      )}

      {/* Render LiveKit room when token is available and user clicked - keep stable to prevent re-connection spam */}
      {token && roomName && serverUrl && userClicked ? (
        <LiveKitStateContext.Provider value={contextValue}>
          <LiveKitRoom
            token={token}
            serverUrl={serverUrl}
            connect={true}
            onConnected={handleRoomConnected}
            onDisconnected={handleRoomDisconnected}
            onError={handleRoomError}
            audio={mode !== 'classroom' && !silentMode} /* No audio in classroom or silent mode */
            video={false}
            options={liveKitOptions}
          >
            <RoomComponentInner />
          </LiveKitRoom>
        </LiveKitStateContext.Provider>
      ) : mode === 'voice' ? (
        <div className="init-container" style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '15px',
          border: '1px dashed #4158D0',
          borderRadius: '8px',
          backgroundColor: 'rgba(65, 88, 208, 0.05)'
        }}>
          {/* Prioritize showing the "Start New Voice Session" button when isDisabled is true */}
          {isDisabled ? (
            <>
              <button
                onClick={async () => {
                  // Generate a new session ID for the new conversation
                  const newSessionId = `session-${Math.random().toString(36).substring(2, 15)}`;

                  // Call the clear-terminated-session endpoint to remove any terminated session flags
                  // This is important when restarting a session
                  try {
                    // First clear the old session from terminated_sessions if needed
                    if (sessionId) {
                      await axios.post('/api/livekit/clear-terminated-session', {
                        session_id: sessionId
                      });
                    }
                  } catch (error) {
                    console.error('Error clearing terminated session:', error);
                    // Continue anyway, as this is not critical
                  }
                  
                  // Update state with new session ID
                  setSessionId(newSessionId);
                  
                  // Reset error state to allow reconnection
                  setError(null);

                  // Reset agent joined state
                  setIsAgentJoined(false);
                  
                  // Set the userInitiatedConnect flag to true to allow connection
                  
                  // Generate a new room name to avoid conflicts with the previous room
                  const newRoomName = `morpheus-voice-${Math.random().toString(36).substring(2, 9)}`;
                  
                  // Important - set these in the correct order with a small delay to ensure React state updates properly
                  // COMMENTED OUT: Redundant - userInitiatedConnect already set by userClicked useEffect
                  // setUserInitiatedConnect(true);
                  setRoomName(newRoomName);
                  
                  // Set connecting state to trigger the useEffect to request a new token
                  setTimeout(() => {
                    setIsConnecting(true);
                  }, 100);
                }}
                style={{
                  width: '60px',
                  height: '60px',
                  borderRadius: Branding.borders.radius.full,
                  background: Branding.gradients.ctaGradient,
                  color: Branding.colors.pureWhite,
                  border: 'none',
                  fontSize: '24px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: `0 auto ${Branding.spacing.md} auto`,
                  cursor: 'pointer',
                  boxShadow: Branding.shadows.md,
                  transition: Branding.transitions.default
                }}
              >
                <span>🎤</span>
              </button>
              <div style={{ fontWeight: 'bold' }}>
                "I'm ready when you are!"
              </div>
              <p style={{ fontSize: '12px', margin: '8px 0 0 0', color: '#666' }}>
                - Mira, your tutor
              </p>
            </>
          ) : !isConnecting && !error && (
            <>
              {/* Always show the Start Voice Session button */}
              <div style={{ textAlign: 'center' }}>
                <button 
                  className="voice-toggle"
                  onClick={async () => {
                    // Always check microphone permissions directly before proceeding
                    const hasPermission = await requestMicrophonePermissions();
                    if (hasPermission) {
                      // Generate a unique room name
                      const newRoomName = `morpheus-voice-${Math.random().toString(36).substring(2, 9)}`;
                      
                      // BATCH ALL STATE UPDATES: Use React.startTransition to prevent cascading renders
                      React.startTransition(() => {
                        setRoomName(newRoomName);
                        // COMMENTED OUT: Redundant - userInitiatedConnect already set by userClicked useEffect
                        // setUserInitiatedConnect(true);
                        
                        // If serverURL is not set, set a default value
                        if (!serverUrl) {
                          setServerUrl(LIVEKIT_URL || 'wss://localhost:7880');
                        }
                        
                        // Set connecting state - remove setTimeout to eliminate delayed render
                        setIsConnecting(true);
                      });
                    }
                  }}
                  disabled={isDisabled}
                  style={{
                    width: '60px',
                    height: '60px',
                    borderRadius: '50%',
                    backgroundColor: '#4158D0',
                    color: 'white',
                    border: 'none',
                    fontSize: '24px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto 10px auto',
                    cursor: 'pointer'
                  }}
                >
                  <span className="voice-toggle-icon">
                    {micPermissionStatus === 'denied' ? '🚫' : '🎤'}
                  </span>
                  {micPermissionStatus !== 'granted' && <span style={{fontSize: '12px', marginLeft: '5px'}}>Enable Mic</span>}
                </button>
                <div className="voice-button-text" style={{ fontWeight: 'bold' }}>
                  "Hi! I'm Mira, your tutor"
                </div>
                <p style={{ fontSize: '12px', margin: '8px 0 0 0', color: '#666' }}>
                  Click to start our conversation
                </p>
              </div>
            </>
          )}
        </div>
      ) : null}

      {/* Toast notification for friend join/leave messages */}
      {toastNotification && (
        <ToastNotification
          message={toastNotification.message}
          type={toastNotification.type}
          duration={toastNotification.duration}
          onClose={handleToastClose}
        />
      )}

      {/* Pricing Modal for Guest Session Limit Paywall */}
      {showPricingModal && (
        <>
          {/* Modal Overlay */}
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 9999,
              backdropFilter: 'blur(4px)',
              animation: 'fadeIn 200ms ease-out'
            }}
            onClick={() => setShowPricingModal(false)}
            role="dialog"
            aria-modal="true"
            aria-labelledby="pricing-modal-title"
          >
            {/* Modal Card */}
            <div
              style={{
                backgroundColor: 'white',
                borderRadius: '12px',
                width: '95vw',
                maxWidth: '1400px',
                maxHeight: '90vh',
                overflow: 'auto',
                boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
                animation: 'slideUp 200ms ease-out',
                position: 'relative'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Close Button */}
              <button
                onClick={() => setShowPricingModal(false)}
                style={{
                  position: 'absolute',
                  top: '20px',
                  right: '20px',
                  background: 'white',
                  border: '1px solid #e5e7eb',
                  borderRadius: '50%',
                  width: '32px',
                  height: '32px',
                  fontSize: '20px',
                  color: '#6b7280',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 150ms',
                  zIndex: 10
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#f3f4f6';
                  e.target.style.color = '#111827';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = 'white';
                  e.target.style.color = '#6b7280';
                }}
                aria-label="Close pricing modal"
              >
                ✕
              </button>

              {/* Pricing Tiers Component */}
              <PricingTiers userTier={userTier} currentUser={user} />
            </div>
          </div>
        </>
      )}
    </div>
  );
});

export default LiveKitVoiceInput;